export type ResultScope = 'all' | 'matches';
export type ArtifactScope = ResultScope | 'search_results';
export type DeliverySourceKind = 'prompt_run' | 'search_snapshot';

export interface ResultLinkTarget {
  videoId: string;
  runId: string | null;
  indexId: string | null;
  segmentId: string | null;
  output: 'segment' | 'video' | null;
  playback: 'segment' | 'video' | null;
  metadata: 'fields' | 'json' | null;
  resultScope: ResultScope | null;
  time: number | null;
  field: string | null;
}

export interface ArtifactLinkTarget {
  exportId: string | null;
  sourceKind: DeliverySourceKind | null;
  sourceId: string | null;
  scope: ArtifactScope;
  download: boolean;
}

const identifier = (value: string | null): string | null => {
  const normalized = value?.trim() || '';
  const hasInvalidCharacter = Array.from(normalized).some((character) => (
    character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127
    || character === '/' || character === '\\'
  ));
  return normalized && normalized.length <= 512 && !hasInvalidCharacter ? normalized : null;
};

export const RESULT_VIEW_KEYS = ['segment', 'output', 'playback', 'metadata', 'result', 't', 'field'];

export const parseResultLink = (pathname: string, search: string): ResultLinkTarget | null => {
  const params = new URLSearchParams(search);
  if (pathname !== '/playground' && pathname !== '/index') return null;
  if (pathname === '/index' && params.get('view') !== 'video') return null;
  if (pathname === '/playground' && (params.get('view') === 'prompt' || params.has('prompt') || params.has('promptNew'))) return null;
  const videoId = identifier(params.get('video'));
  const indexId = pathname === '/index' ? identifier(params.get('index')) : null;
  if (!videoId || (pathname === '/index' && !indexId)) return null;
  const rawTime = params.get('t');
  const time = rawTime !== null && rawTime.trim() !== '' ? Number(rawTime) : NaN;
  return {
    videoId, indexId,
    runId: identifier(params.get('run')),
    segmentId: identifier(params.get('segment')),
    output: params.get('output') === 'video' ? 'video' : params.get('output') === 'segment' ? 'segment' : null,
    playback: params.get('playback') === 'video' ? 'video' : params.get('playback') === 'segment' ? 'segment' : null,
    metadata: params.get('metadata') === 'json' ? 'json' : params.get('metadata') === 'fields' ? 'fields' : null,
    resultScope: params.get('result') === 'matches' ? 'matches' : params.get('result') === 'all' ? 'all' : null,
    time: Number.isFinite(time) && time >= 0 ? time : null,
    field: params.get('field')?.trim().slice(0, 500) || null,
  };
};

export const resultViewParams = (target: ResultLinkTarget | null) => ({
  segment: target?.segmentId || null,
  output: target?.output || null,
  playback: target?.playback || null,
  metadata: target?.metadata || null,
  result: target?.resultScope || null,
  t: target?.time ?? null,
  field: target?.field || null,
});

export const buildResultLink = (target: ResultLinkTarget): string => {
  const pathname = target.indexId && target.indexId !== 'playground' ? '/index' : '/playground';
  const params = new URLSearchParams(pathname === '/index'
    ? { index: target.indexId!, view: 'video' }
    : { step: 'studio' });
  params.set('video', target.videoId);
  if (target.runId) params.set('run', target.runId);
  Object.entries(resultViewParams(target)).forEach(([key, value]) => {
    if (value !== null) params.set(key, String(value));
  });
  return `${pathname}?${params}`;
};

export const parseArtifactLink = (search: string): ArtifactLinkTarget | null => {
  const params = new URLSearchParams(search);
  if (params.get('tab') !== 'exports') return null;
  const exportId = identifier(params.get('export'));
  const rawKind = params.get('source_kind');
  const sourceKind = rawKind === 'prompt_run' || rawKind === 'search_snapshot' ? rawKind : null;
  const sourceId = sourceKind ? identifier(params.get('source_id')) : null;
  if (!exportId && !sourceId) return null;
  const rawScope = params.get('scope');
  const scope = rawScope === 'matches' || rawScope === 'search_results' ? rawScope : 'all';
  if (sourceId && ((sourceKind === 'search_snapshot') !== (scope === 'search_results'))) return null;
  return { exportId, sourceKind, sourceId, scope, download: params.get('action') === 'download' };
};

export const buildArtifactLink = (target: ArtifactLinkTarget): string => {
  const params = new URLSearchParams({ tab: 'exports' });
  if (target.exportId) params.set('export', target.exportId);
  if (target.sourceKind && target.sourceId) {
    params.set('source_kind', target.sourceKind);
    params.set('source_id', target.sourceId);
  }
  params.set('scope', target.scope);
  if (target.download) params.set('action', 'download');
  return `/jobs?${params}`;
};

/** Login stays on an internal result destination; no user-supplied redirect URL. */
export const isResultDestination = (pathname: string, search: string): boolean => (
  Boolean(parseResultLink(pathname, search))
  || (pathname === '/jobs' && Boolean(parseArtifactLink(search)))
);
